import type {
  SoftphoneDiagnostic,
  SoftphoneQualityDiagnostic,
  SoftphoneStartFailureDiagnostic,
  SoftphoneTerminationDiagnostic,
} from './softphoneClient.types';

/**
 * What a surface should send to its error tracker for one diagnostic — the decision of WHAT to
 * report, kept here so the dashboard and the extension make the same one and only differ in HOW
 * they reach Sentry (the dashboard's SDK directly, the extension's background bridge).
 *
 *  - `breadcrumb`: context for a later event, never an issue of its own.
 *  - `exception`: a failure, grouped by `name` + tags.
 *  - `message`: a degraded-but-finished call, grouped by its warning combination.
 */
export type SoftphoneReport =
  | {
      kind: 'breadcrumb';
      category: 'softphone.quality' | 'softphone.start';
      level: 'warning' | 'info';
      message: string;
      data: Record<string, unknown>;
    }
  | {
      kind: 'exception';
      name: 'SoftphoneCallFailure' | 'SoftphoneStartFailure';
      message: string;
      tags: Record<string, string>;
      extra: Record<string, unknown>;
    }
  | {
      kind: 'message';
      name: 'SoftphoneCallDegraded';
      level: 'warning';
      message: string;
      tags: Record<string, string>;
      extra: Record<string, unknown>;
      fingerprint: string[];
    };

/**
 * Decide how one diagnostic reaches the error tracker, or null for the ones that shouldn't
 * (a clean call that ended normally).
 *
 * The carrier codes that actually explain a broken call (Twilio 31000 TwiML fetch failure, or the
 * instant hangup a rejected `<Dial>` produces in the browser) used to reach `console.error` and
 * nowhere else: a production outage in which every outbound call failed ran for five days with no
 * server-side signal at all, and had to be reconstructed from the carrier's own logs by hand.
 *
 * Searchable by the `softphone*` tags: `softphoneWarnings:*high-packet-loss*` lists every call that
 * lost audio to the network, `softphoneReconnects:>0` every call that went silent while the SDK
 * renegotiated, `softphoneStage:dial` every call that never got past the microphone prompt.
 */
export function buildSoftphoneReport(diagnostic: SoftphoneDiagnostic): SoftphoneReport | null {
  switch (diagnostic.kind) {
    case 'quality':
      return qualityBreadcrumb(diagnostic);
    case 'start-failure':
      return startFailureReport(diagnostic);
    case 'termination':
      return terminationReport(diagnostic);
  }
}

/**
 * Whether a mid-call event is something going wrong (as opposed to recovering or a harmless
 * change): a warning raised, a reconnect starting, or the device the call was using vanishing.
 * The one decision behind both the console level and the breadcrumb level.
 */
export function isRaisedQualityEvent(diagnostic: SoftphoneQualityDiagnostic): boolean {
  return (
    diagnostic.event === 'warning' ||
    diagnostic.event === 'reconnecting' ||
    (diagnostic.event === 'device-change' && (diagnostic.detail?.lostDevices?.length ?? 0) > 0)
  );
}

function qualityBreadcrumb(diagnostic: SoftphoneQualityDiagnostic): SoftphoneReport {
  const raised = isRaisedQualityEvent(diagnostic);
  const label = diagnostic.name != null ? `${diagnostic.event} ${diagnostic.name}` : diagnostic.event;
  return {
    kind: 'breadcrumb',
    category: 'softphone.quality',
    level: raised ? 'warning' : 'info',
    message: `Softphone ${label} at ${diagnostic.secondsIntoCall}s`,
    data: {
      voiceCallId: diagnostic.voiceCallId,
      event: diagnostic.event,
      name: diagnostic.name,
      secondsIntoCall: diagnostic.secondsIntoCall,
      ...(diagnostic.detail?.threshold ? { threshold: diagnostic.detail.threshold } : {}),
      ...(diagnostic.detail?.values ? { values: diagnostic.detail.values } : {}),
      ...(diagnostic.detail?.lostDevices ? { lostDevices: diagnostic.detail.lostDevices } : {}),
    },
  };
}

function startFailureReport(diagnostic: SoftphoneStartFailureDiagnostic): SoftphoneReport {
  const data = {
    voiceCallId: diagnostic.voiceCallId,
    stage: diagnostic.stage,
    errorName: diagnostic.errorName,
    code: diagnostic.code,
    message: diagnostic.message,
  };
  // The server declining to start a call is a refusal it already logged with the reason (no
  // number on file, calling disabled, a teammate got there first): context, not a fault here.
  if (diagnostic.stage === 'server') {
    return {
      kind: 'breadcrumb',
      category: 'softphone.start',
      level: 'warning',
      message: `Softphone start refused by server: ${diagnostic.message}`,
      data,
    };
  }
  return {
    kind: 'exception',
    name: 'SoftphoneStartFailure',
    message: `Softphone ${diagnostic.stage} failed${diagnostic.code != null ? ` ${diagnostic.code}` : ''}: ${diagnostic.message}`,
    tags: {
      softphoneStage: diagnostic.stage,
      softphoneErrorName: diagnostic.errorName ?? 'none',
      softphoneCode: diagnostic.code != null ? String(diagnostic.code) : 'none',
    },
    extra: data,
  };
}

function qualityTags(quality: SoftphoneTerminationDiagnostic['quality']): Record<string, string> {
  if (quality == null) return {};
  return {
    softphoneDegraded: String(quality.degraded),
    // Joined so one tag filter finds every call that hit a given warning.
    softphoneWarnings: quality.warnings.length > 0 ? quality.warnings.join(',') : 'none',
    softphoneReconnects: String(quality.reconnects),
    softphoneActiveDeviceLost: String(quality.activeDeviceLost),
    softphoneCodec: quality.codec ?? 'unknown',
    softphoneEdge: quality.edge ?? 'unknown',
    softphoneInputDevice: quality.inputDevice ?? 'unknown',
  };
}

function terminationReport(diagnostic: SoftphoneTerminationDiagnostic): SoftphoneReport | null {
  const { quality } = diagnostic;
  const tags = {
    softphoneEvent: diagnostic.event,
    // Tagged so "every call failing with one code" is a single filter in Sentry.
    softphoneCode: diagnostic.code != null ? String(diagnostic.code) : 'none',
    softphoneBecameActive: String(diagnostic.becameActive),
    ...qualityTags(quality),
  };
  const extra = {
    voiceCallId: diagnostic.voiceCallId,
    becameActive: diagnostic.becameActive,
    code: diagnostic.code,
    description: diagnostic.description,
    explanation: diagnostic.explanation,
    carrierMessage: diagnostic.message,
    quality,
  };

  if (diagnostic.abnormal) {
    const codeLabel = diagnostic.code != null ? ` ${diagnostic.code}` : '';
    const detail = diagnostic.description ?? diagnostic.explanation ?? diagnostic.message ?? 'no carrier detail';
    return {
      kind: 'exception',
      name: 'SoftphoneCallFailure',
      message: `Softphone ${diagnostic.event}${codeLabel}: ${detail}`,
      tags,
      extra,
    };
  }

  // A normal hangup after a clean conversation is not a fault; only a degraded call reports.
  if (quality == null || !quality.degraded) return null;

  const parts = [
    quality.warnings.length > 0 ? `warnings ${quality.warnings.join(', ')}` : null,
    quality.reconnects > 0 ? `${quality.reconnects} reconnect(s), ${quality.reconnectingSec}s silent` : null,
    quality.activeDeviceLost > 0 ? `${quality.activeDeviceLost} active device(s) lost` : null,
  ].filter((p) => p != null);
  return {
    kind: 'message',
    name: 'SoftphoneCallDegraded',
    level: 'warning',
    message: `Softphone call degraded: ${parts.join('; ')}`,
    tags,
    extra,
    // One issue per combination of causes, so a packet-loss call and a mic-went-flat call
    // don't share a thread.
    fingerprint: [
      'softphone-call-degraded',
      ...quality.warnings,
      quality.reconnects > 0 ? 'reconnect' : 'no-reconnect',
      quality.activeDeviceLost > 0 ? 'device-lost' : 'device-kept',
    ],
  };
}
